import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import vatAPI from "../api/vatAPI";
import { useAuth } from "../context/AuthContext";
import ChartOfAccountsSearch from "./ChartOfAccountsSearch";
import CustomerSearch from "./CustomerSearch";
import AtcSearchSelect from "./AtcSearchSelect";
import Button from "./Button";
import MoneyInput from "./MoneyInput";
import RestoreDraftModal from "./RestoreDraftModal";
import SaveOutcomePanel from "./SaveOutcomePanel";
import DraftStatusIndicator from "./DraftStatusIndicator";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import notify from "../lib/notify";
import useGuardedMutation from "../hooks/useGuardedMutation";
import useEncodingDraft from "../hooks/useEncodingDraft";
import { DRAFT_SCREENS } from "../utils/encodingDraft";
import { setPendingIdempotencyKey } from "../utils/idempotencyKey";
import { isCorporateFiler } from "../utils/sawtFormType";
import {
  classifyRows,
  confirmedRowIdsFromEcho,
  newClientRowId,
  rowErrorsFromLineErrors,
  rowsAfterSave,
  withClientRowIds,
} from "../utils/encodingGrid";

// Reused across every row/render instead of instantiating Intl.NumberFormat per cell
const moneyFormatter = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatMoney = (num) => moneyFormatter.format(Number(num) || 0);

const ALL_MONTH_OPTIONS = [
  { value: 1, label: "Jan" }, { value: 2, label: "Feb" }, { value: 3, label: "Mar" },
  { value: 4, label: "Apr" }, { value: 5, label: "May" }, { value: 6, label: "Jun" },
  { value: 7, label: "Jul" }, { value: 8, label: "Aug" }, { value: 9, label: "Sep" },
  { value: 10, label: "Oct" }, { value: 11, label: "Nov" }, { value: 12, label: "Dec" },
];

const getCustomerAddress = (customer) => {
  if (!customer) return '';
  // Build from individual fields if string address is available
  if (typeof customer.address === 'string' && customer.address.trim()) {
    const parts = [customer.address.trim(), customer.city, customer.province, customer.zipCode].filter(Boolean);
    return parts.length > 1 ? parts.join(', ') : customer.address.trim();
  }
  if (typeof customer.businessAddress === 'string' && customer.businessAddress.trim()) return customer.businessAddress.trim();
  if (typeof customer.contactInfo === 'string' && customer.contactInfo.trim()) return customer.contactInfo.trim();
  // Check for structured address object
  const addrObj = customer.contactInfo?.address || customer.address || customer.businessAddress;
  if (addrObj && typeof addrObj === 'object') {
    return [addrObj.building, addrObj.street, addrObj.substreet, addrObj.barangay, addrObj.city || addrObj.cityMunicipality || addrObj.district, addrObj.province, addrObj.zipCode || addrObj.zip].filter(Boolean).join(', ');
  }
  // Fallback to top-level individual fields
  const parts = [customer.city, customer.province, customer.zipCode].filter(Boolean);
  return parts.join(', ') || '';
};

const MIN_ROWS = 5;

// The blank row, in one place: used for the initial grid, Clear All, and the
// top-up after a save. Ids come from encodingGrid so a topped-up row can never
// collide with a row kept from before the save.
const buildEmptyRow = (defaultMonth) => ({
  id: newClientRowId(),
  month: defaultMonth,
  grossAmount: "",
  vatAmount: "",
  netAmount: "",
  vatExempt: "",
  description: "",
  chartOfAccount: null,
  chartOfAccountName: "",
  customer: null,
  customerName: "",
  customerTin: "",
  customerClassification: "",
  customerAddress: "",
  atcs: [null],
  isService: false,
  isZeroRated: false,
  customerTinLocked: false,
  customerAddressLocked: false,
});

const buildEmptyRows = (defaultMonth) => Array.from({ length: MIN_ROWS }, () => buildEmptyRow(defaultMonth));

// The server keeps only schema fields, so a saved record comes back without the
// client row id. A short echo is matched back to the sent rows on these fields.
const vatRecordMatchKey = (record) =>
  [
    Number(record?.month),
    String(record?.description || "").trim(),
    Number(record?.grossAmount || 0).toFixed(2),
    Number(record?.vatAmount || 0).toFixed(2),
  ].join("|");

// Module scope so the autosave hook holds a stable reference: the draft layer
// and the save path must agree on what "this row is untouched" means. Fields
// are read defensively — a restored draft is validated before it gets here, but
// this must never be the thing that breaks a mount.
const isRowEmpty = (row) =>
  !(row?.description || "").trim() &&
  !row?.grossAmount &&
  !row?.vatAmount &&
  !row?.netAmount &&
  !row?.vatExempt;

const VATOutputForm = ({ client, onRecordAdded, selectedQuarter, selectedYear, selectedMonth }) => {
  const [rows, setRows] = useState([]);
  const [clearSearchTrigger, setClearSearchTrigger] = useState(0);
  // Outcome of the last submit. VAT posts the whole grid in one request that
  // the server treats as all-or-nothing, so there is one entry here, not one
  // per row — but "we never heard back" is still reported as its own state
  // rather than being flattened into failure.
  const [outcomes, setOutcomes] = useState([]);
  // Server errors placed on the rows they name ({ [rowId]: message }), from the
  // last submit only.
  const [rowErrors, setRowErrors] = useState({});
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const [lastAddedRowIds, setLastAddedRowIds] = useState([]);
  const rowWrapperRefs = useRef({});
  const monthSelectRefs = useRef({});

  const toggleRowExpanded = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (lastAddedRowIds.length === 0) return;
    const firstId = lastAddedRowIds[0];
    const node = rowWrapperRefs.current[firstId];
    if (node?.scrollIntoView) node.scrollIntoView({ behavior: "smooth", block: "center" });
    monthSelectRefs.current[firstId]?.focus();
    setLastAddedRowIds([]);
  }, [lastAddedRowIds]);

  // FY from header/context (preferred)
  const fiscalYearNum = useMemo(() => {
    const fromSelected = parseInt(selectedYear, 10);
    if (fromSelected && !isNaN(fromSelected)) return fromSelected;

    const fyKey = client?._id ? `activeFiscalYear:${client._id}` : null;
    const headerFY = fyKey ? parseInt(localStorage.getItem(fyKey), 10) : null;

    return headerFY && !isNaN(headerFY) ? headerFY : null;
  }, [selectedYear, client?._id]);

  const getDefaultMonthForRow = useCallback(() => {
    // Prefer selectedMonth if provided, else current month
    const m = parseInt(selectedMonth, 10);
    if (m && !isNaN(m)) return m;
    return new Date().getMonth() + 1;
  }, [selectedMonth]);

  // Initialize rows once
  useEffect(() => {
    if (rows.length === 0) {
      setRows(buildEmptyRows(getDefaultMonthForRow()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If selectedMonth changes, gently apply it only to rows that have no month yet
  useEffect(() => {
    const m = parseInt(selectedMonth, 10);
    if (!m || isNaN(m)) return;

    setRows((prev) => prev.map((r) => (!r.month ? { ...r, month: m } : r)));
  }, [selectedMonth]);

  const handleChartOfAccountSelect = useCallback((account, rowId) => {
    if (!account) {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === rowId
            ? { ...row, chartOfAccount: null, chartOfAccountName: "", description: "" }
            : row
        )
      );
      return;
    }

    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId
          ? {
              ...row,
              chartOfAccount: account._id,
              chartOfAccountName: `${account.accountNumber} - ${account.accountName}`,
              description: account.accountName,
            }
          : row
      )
    );
  }, []);

  const handleDescriptionInput = useCallback((value, rowId) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== rowId) return row;

        const updatedRow = { ...row, description: value };

        // If user types and there isn't a COA label, keep it as free-text
        if (value && !row.chartOfAccountName) {
          updatedRow.chartOfAccount = null;
          updatedRow.chartOfAccountName = "";
        }

        return updatedRow;
      })
    );
  }, []);

  const handleCustomerNameChange = useCallback((rowId, value) => {
    setRows((prevRows) => prevRows.map((row) => (row.id === rowId ? { ...row, customerName: value } : row)));
  }, []);

  const handleCustomerTinChange = useCallback((rowId, value) => {
    setRows((prevRows) => prevRows.map((row) => (row.id === rowId ? { ...row, customerTin: value } : row)));
  }, []);

  const handleCustomerSelect = useCallback((rowId, customer) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== rowId) return row;

        if (!customer) {
          return {
            ...row,
            customer: null,
            customerName: "",
            customerTin: "",
            customerClassification: "",
            customerAddress: "",
        atcs: [null],
            customerTinLocked: false,
            customerAddressLocked: false,
          };
        } else {
          const tin = customer.tin || "";
          const address = getCustomerAddress(customer);
          return {
            ...row,
            customer: customer,
            customerName: customer.name || "",
            customerTin: tin,
            customerClassification: customer.businessType === 'corporation' ? 'corporation' : 'individual',
            customerAddress: address,
            // Snapshot at selection time — a field starts locked only if the linked
            // record already had a value. It must NOT re-derive from live content,
            // or the field would lock itself after the user's first keystroke.
            customerTinLocked: !!tin,
            customerAddressLocked: !!address,
          };
        }
      })
    );
  }, []);

  const handleCustomerClassificationChange = useCallback((rowId, value) => {
    setRows((prevRows) => prevRows.map((row) => (row.id === rowId ? { ...row, customerClassification: value } : row)));
  }, []);

  const handleCustomerAddressChange = useCallback((rowId, value) => {
    setRows((prevRows) => prevRows.map((row) => (row.id === rowId ? { ...row, customerAddress: value } : row)));
  }, []);

  // One transaction can carry MORE THAN ONE withholding. A government customer
  // withholds expanded withholding tax AND, separately, the 5% creditable VAT
  // withholding (WV010 goods / WV020 services, RR 4-2021) on the same sale — two
  // ATCs, two certificate lines, one encoding. `atcs` is therefore a list; the
  // first slot is always rendered so the ordinary single-ATC case is unchanged.
  const handleAtcChange = useCallback((rowId, index, atc) => {
    setRows((prevRows) => prevRows.map((row) => {
      if (row.id !== rowId) return row;
      const atcs = [...(row.atcs || [null])];
      atcs[index] = atc;
      return { ...row, atcs };
    }));
  }, []);

  const handleAddAtc = useCallback((rowId) => {
    setRows((prevRows) => prevRows.map((row) => (
      row.id === rowId ? { ...row, atcs: [...(row.atcs || [null]), null] } : row
    )));
  }, []);

  const handleRemoveAtc = useCallback((rowId, index) => {
    setRows((prevRows) => prevRows.map((row) => {
      if (row.id !== rowId) return row;
      const atcs = (row.atcs || [null]).filter((_, i) => i !== index);
      return { ...row, atcs: atcs.length ? atcs : [null] };
    }));
  }, []);

  const handleIsServiceChange = useCallback((rowId, checked) => {
    setRows((prevRows) => prevRows.map((row) => (row.id === rowId ? { ...row, isService: checked } : row)));
  }, []);

  const handleIsZeroRatedChange = useCallback((rowId, checked) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== rowId) return row;
        return {
          ...row,
          isZeroRated: checked,
          isVatExempt: checked ? false : row.isVatExempt,
          vatAmount: checked ? '0.00' : row.vatAmount,
          netAmount: checked ? (parseFloat(row.grossAmount || 0).toFixed(2)) : row.netAmount,
        };
      })
    );
  }, []);

  const handleMonthChange = useCallback((rowId, value) => {
    const monthNum = parseInt(value, 10);
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, month: monthNum } : r)));
  }, []);

  // VAT calculation (12% VAT inclusive)
  const calculateVAT = (grossAmount) => {
    if (!grossAmount || isNaN(grossAmount) || parseFloat(grossAmount) <= 0) {
      return { vat: 0, net: 0, vatExempt: 0 };
    }
    const gross = parseFloat(grossAmount);
    const vat = (gross / 1.12) * 0.12;
    const net = gross - vat;
    return { vat, net, vatExempt: 0 };
  };

  const handleGrossAmountChange = (id, value) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== id) return row;

        if (row.vatExempt) {
          const { vat, net } = calculateVAT(value);
          return { ...row, grossAmount: value, vatAmount: vat.toFixed(2), netAmount: net.toFixed(2), vatExempt: "", isZeroRated: false };
        }

        if (row.isZeroRated) {
          const gross = parseFloat(value) || 0;
          return {
            ...row,
            grossAmount: value,
            vatAmount: "0.00",
            netAmount: gross.toFixed(2),
          };
        }

        const { vat, net, vatExempt } = calculateVAT(value);
        return {
          ...row,
          grossAmount: value,
          vatAmount: vat.toFixed(2),
          netAmount: net.toFixed(2),
          vatExempt: vatExempt.toFixed(2),
        };
      })
    );
  };

  const handleVatExemptChange = (id, value) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== id) return row;

        if (value && !isNaN(value) && parseFloat(value) > 0) {
          return {
            ...row,
            vatExempt: value,
            isVatExempt: true,
            isZeroRated: false,
            grossAmount: value,
            vatAmount: "0.00",
            netAmount: value,
          };
        }
        return {
          ...row,
          vatExempt: value,
        };
      })
    );
  };

  const addRows = () => {
    const newRows = Array.from({ length: 5 }, () => ({
      id: newClientRowId(),
      month: getDefaultMonthForRow(),
      grossAmount: "",
      vatAmount: "",
      netAmount: "",
      vatExempt: "",
      description: "",
      chartOfAccount: null,
      chartOfAccountName: "",
      customer: null,
      customerName: "",
      customerTin: "",
      customerClassification: "",
      customerAddress: "",
      isService: false,
      isZeroRated: false,
      customerTinLocked: false,
      customerAddressLocked: false,
    }));
    setRows((prev) => [...prev, ...newRows]);
    setLastAddedRowIds(newRows.map((r) => r.id));
  };

  const removeRow = (id) => {
    if (rows.length > MIN_ROWS) setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const clearAllRows = () => {
    setRows(buildEmptyRows(getDefaultMonthForRow()));
    setClearSearchTrigger((prev) => prev + 1);
    // Clear All is a deliberate discard, so the autosaved copy goes too —
    // otherwise the next visit would offer to restore what was just thrown away.
    draft.discardDraft();
  };

  // A row with some data entered but missing what handleSave requires to persist it
  const isRowIncomplete = (row) => {
    if (isRowEmpty(row)) return false;
    const hasAmount = parseFloat(row.grossAmount) > 0 || parseFloat(row.vatExempt) > 0;
    return !(row.description.trim() && row.month && hasAmount);
  };

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        const hasData = row.description.trim() || row.grossAmount || row.vatAmount || row.netAmount || row.vatExempt;
        if (!hasData) return acc;

        acc.gross += parseFloat(row.grossAmount) || 0;
        acc.vat += parseFloat(row.vatAmount) || 0;
        acc.net += parseFloat(row.netAmount) || 0;
        acc.vatExempt += parseFloat(row.vatExempt) || 0;
        return acc;
      },
      { gross: 0, vat: 0, net: 0, vatExempt: 0 }
    );
  }, [rows]);

  const filledRowsCount = useMemo(() => rows.filter((r) => !isRowEmpty(r)).length, [rows]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const incompleteRowsCount = useMemo(() => rows.filter(isRowIncomplete).length, [rows]);

  const { user } = useAuth();
  const tenantId = user?.tenantId || localStorage.getItem("tenantId");
  const storeId = localStorage.getItem("storeId");

  const isClientAvailable = !!client?._id;
  const isFiscalYearAvailable = !!fiscalYearNum;

  const draft = useEncodingDraft({
    screen: DRAFT_SCREENS.VAT_OUTPUT,
    clientId: client?._id,
    fiscalYear: fiscalYearNum,
    rows,
    isRowEmpty,
  });

  const handleRestoreDraft = () => {
    const restored = draft.restoreDraft();
    // clearSearchTrigger is deliberately not bumped — the restored rows carry
    // their own ids, so the account and customer pickers remount and repopulate
    // from the restored names; clearing would blank what we just restored.
    // withClientRowIds: drafts written before every row had a unique id still load.
    if (restored?.length) setRows(withClientRowIds(restored));
  };

  const handleSave = async () => {
    const resolvedTenantId = user?.tenantId || localStorage.getItem("tenantId");
    console.log('[VAT Output] tenantId at save:', resolvedTenantId, '| user?.tenantId:', user?.tenantId, '| localStorage:', localStorage.getItem('tenantId'));

    if (!isClientAvailable) {
      notify.error("Couldn't save", "Select a client first.");
      return;
    }

    if (!isFiscalYearAvailable) {
      notify.error("Couldn't save", "Select a fiscal year from the header first.");
      return;
    }

    if (!resolvedTenantId) {
      notify.error("Couldn't save", "Tenant information is missing — log in again or reselect your tenant.");
      return;
    }

    // Only complete rows are sent. Incomplete rows stay in the grid (and the
    // draft), highlighted; untouched blank rows are neither sent nor flagged.
    const { toSave: validRows, incomplete } = classifyRows(rows, isRowIncomplete, isRowEmpty);
    setRowErrors({});

    if (validRows.length === 0) {
      notify.error(
        "Couldn't save",
        incomplete.length > 0
          ? `${incomplete.length} row${incomplete.length !== 1 ? "s are" : " is"} incomplete — each needs a description, a month and an amount. Nothing was sent.`
          : "Add at least one record before saving."
      );
      return;
    }
    const keptNote =
      incomplete.length > 0
        ? ` ${incomplete.length} incomplete row${incomplete.length !== 1 ? "s were" : " was"} kept in the grid.`
        : "";

    const accountingClientId = client?._id || null;

    const buildRecords = (transactionDate) => validRows.map((row) => {
      const isVatExempt = row.vatExempt && parseFloat(row.vatExempt) > 0;
      const vatExemptAmount = parseFloat(row.vatExempt || 0);

      const base = {
        recordType: "output", // ✅ OUTPUT VAT
        month: parseInt(row.month, 10),
        chartOfAccount: row.chartOfAccount || null,
        description: row.description,
        transactionDate,
        status: "draft",
        tenant: resolvedTenantId,
        store: storeId || null,
        accountingClient: accountingClientId || null,
        customer: row.customer || null,
        customerName: row.customerName || '',
        customerTin: row.customerTin || '',
        customerClassification: row.customerClassification || '',
        customerAddress: row.customerAddress || '',
        // Optional: null means no 2307 was issued against this sale.
        // Only the chosen ATC ids are sent. The rate, base and tax are
        // resolved and computed server-side against the ATC table, so the
        // browser can never put its own rate on a BIR certificate.
        withholdings: (row.atcs || [])
          .filter(Boolean)
          .map((a) => ({ atcId: a._id })),
        isService: Boolean(row.isService),
        isZeroRated: Boolean(row.isZeroRated),
      };

      if (isVatExempt) {
        return {
          ...base,
          grossAmount: vatExemptAmount,
          vatAmount: 0,
          netAmount: vatExemptAmount,
          vatExemptAmount: vatExemptAmount,
          isVatExempt: true,
        };
      }

      return {
        ...base,
        grossAmount: parseFloat(row.grossAmount),
        vatAmount: parseFloat(row.vatAmount || 0),
        netAmount: parseFloat(row.netAmount || 0),
        vatExemptAmount: 0,
        isVatExempt: false,
      };
    });

    const groupId = `vat-output-fy${fiscalYearNum}`;
    const outcomeLabel = `Output VAT · FY ${fiscalYearNum}`;

    // One key per submit attempt, reused verbatim on a retry of the same
    // rows, so resubmitting after a dropped response replays the original
    // request instead of inserting the whole batch a second time.
    //
    // The key is derived from the rows WITHOUT transactionDate, and the date
    // is then taken from the key itself: stamping `new Date()` per attempt
    // would send a different body under an unchanged key, which the backend
    // rejects as key reuse — so a retry would never actually replay.
    const { key: idempotencyKey, stamp } = draft.keyForGroup(groupId, buildRecords(null));
    const recordsPayload = buildRecords(stamp);
    setPendingIdempotencyKey(idempotencyKey);

    let savedRecords = [];

    try {
      // ✅ vatAPI expects payload object with { records: [...] }
      const response = await vatAPI.createRecords(client._id, { records: recordsPayload }, fiscalYearNum);

      savedRecords = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [response?.data || response].filter(Boolean);

      // The server builds every document before inserting, so a 2xx means the
      // whole batch landed. Reconcile the count anyway rather than assume it:
      // a short echo is reported as partial instead of being rounded up to
      // "Saved".
      const echoedAll =
        !Array.isArray(response?.data) || response.data.length >= recordsPayload.length;

      // Remove exactly the confirmed rows; everything else (incomplete rows,
      // blank rows, unconfirmed rows) stays where it is. clearSearchTrigger is
      // deliberately not bumped: it would blank the pickers on the kept rows,
      // and the topped-up rows have fresh ids so their pickers start empty.
      const removeConfirmed = (confirmedIds) => {
        setRows((prev) => rowsAfterSave(prev, confirmedIds, MIN_ROWS, () => buildEmptyRow(getDefaultMonthForRow())));
        draft.clearConfirmedRows(confirmedIds);
      };

      if (echoedAll) {
        setOutcomes([
          {
            id: groupId,
            label: outcomeLabel,
            status: "saved",
            sentCount: recordsPayload.length,
            savedCount: recordsPayload.length,
          },
        ]);

        removeConfirmed(validRows.map((row) => row.id));
        draft.releaseKey(groupId);

        const count = validRows.length;
        notify.success("Saved", `${count} output VAT record${count !== 1 ? "s" : ""} for FY ${fiscalYearNum}.${keptNote}`);
      } else {
        // Only the rows the echo accounts for are removed; the rest stay as
        // typed. The key is kept: the unconfirmed rows are still candidates
        // for a replay of this request.
        const confirmedIds = confirmedRowIdsFromEcho(
          validRows.map((row, index) => ({ id: row.id, record: recordsPayload[index] })),
          response.data,
          vatRecordMatchKey
        );
        removeConfirmed(confirmedIds);

        setOutcomes([
          {
            id: groupId,
            label: outcomeLabel,
            status: "partial",
            sentCount: recordsPayload.length,
            savedCount: response.data.length,
            message:
              "The server confirmed fewer records than were sent. The confirmed rows were removed; the rest are still below — check the Records tab before saving them again.",
          },
        ]);
        notify.error(
          "Partly saved",
          "The server confirmed fewer records than were sent — see the details above the grid."
        );
      }
    } catch (error) {
      console.error("Error saving output VAT records:", error);

      // An error carrying a response means the server rejected the batch and
      // nothing was written — safe to fix and resubmit. No response at all
      // means the request may well have succeeded, so the rows stay put and
      // we say plainly that we don't know.
      const hasResponse = Boolean(error?.response) || typeof error === "string";
      const message = error?.message || (typeof error === "string" ? error : null);

      // Per-line errors (when the server sends them) go on the rows they name.
      setRowErrors(rowErrorsFromLineErrors(validRows.map((row) => row.id), error?.lineErrors));

      setOutcomes([
        {
          id: groupId,
          label: outcomeLabel,
          status: hasResponse ? "failed" : "unknown",
          sentCount: recordsPayload.length,
          message: hasResponse
            ? message || "The server rejected this batch — nothing was saved."
            : "No response from the server — these records may or may not have been saved.",
        },
      ]);

      notify.error(
        hasResponse ? "Couldn't save" : "Save unconfirmed",
        hasResponse
          ? `${message || "Unknown error — try again."} Your rows are still here.`
          : "No response from the server. Your rows are still here and autosaved — check the Records tab before saving again."
      );
      return;
    }

    // Kept out of the save's own error handling: this refetches and auto-posts
    // to GL, and a failure here says nothing about whether the records saved.
    // Folding the two together is what used to report "Couldn't save" for
    // records that were already stored — with the grid already cleared.
    try {
      if (onRecordAdded) {
        for (const savedRecord of savedRecords) {
          await onRecordAdded(savedRecord);
        }
      }
    } catch (refreshError) {
      console.error("Post-save refresh failed:", refreshError);
      notify.error(
        "Saved — refresh failed",
        "The records were saved, but the list could not be refreshed. Reload the page to see them."
      );
    }
  };

  const { mutate: runSave, isPending: loading } = useGuardedMutation(handleSave);

  // The request itself has its own error handling above; this catches anything
  // that goes wrong around it (building the payload, say) so it surfaces as a
  // message instead of an unhandled rejection that leaves the button spinning.
  const runSaveSafely = () => {
    runSave()?.catch((error) => {
      console.error("Unexpected error while saving output VAT records:", error);
      notify.error(
        "Couldn't save",
        "Something went wrong before the records could be sent. Your rows are still here."
      );
    });
  };

  return (
    <div className="p-6">
      <RestoreDraftModal
        draft={draft.pendingDraft}
        onRestore={handleRestoreDraft}
        onDiscard={draft.discardDraft}
        screenLabel="Output VAT"
      />

      <div className="mb-6">
        <h3 className="text-lg font-medium text-ink">Output VAT records</h3>
        <p className="text-ink-body mt-1">VAT on sales / receipts</p>

        {outcomes.length > 0 && (
          <div className="mt-4">
            <SaveOutcomePanel outcomes={outcomes} onDismiss={() => setOutcomes([])} />
          </div>
        )}

        {!isClientAvailable && (
          <div className="mt-2 p-3 bg-danger-50 border border-danger-200 rounded-lg">
            <div className="flex items-center text-danger-500">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm font-medium">No client selected. Please select a client to save records.</span>
            </div>
          </div>
        )}

        {/* FY status */}
        <div className="mt-3 text-sm">
          <span className="text-ink-body">Fiscal Year: </span>
          <span className={`font-medium ${isFiscalYearAvailable ? "text-ink" : "text-danger-500"}`}>
            {isFiscalYearAvailable ? fiscalYearNum : "Not selected (use header)"}
          </span>
        </div>
      </div>

      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
        <div className="flex items-center text-sm text-primary-800">
          <svg className="w-4 h-4 mr-2 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <span className="font-mono">Auto-Calculation: VAT = (Gross Amount / 1.12) × 0.12</span>
        </div>
      </div>

      {/* Records Table */}
      <div className="overflow-x-auto border border-ink-line rounded-lg">
        <table className="min-w-full divide-y divide-ink-line">
          <thead className="bg-ink-canvas">
            <tr>
              <th className="w-10 px-2 py-3"></th>
              <th className="px-3 py-3 text-left text-xs font-medium text-ink-body uppercase tracking-wider w-[9%]">Month</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-ink-body uppercase tracking-wider w-[26%]">Description / chart of account</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-ink-body uppercase tracking-wider w-[19%]">Customer</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-ink-body uppercase tracking-wider w-[13%]">Gross sales</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-ink-body uppercase tracking-wider w-[13%]">Output VAT</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-ink-body uppercase tracking-wider w-[13%]">VAT exempt</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-ink-body uppercase tracking-wider w-[6%]">Action</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-ink-line">
            {rows.flatMap((row) => {
              const isExempt = row.vatExempt && parseFloat(row.vatExempt) > 0;
              const isExpanded = expandedRows.has(row.id);
              const rowError = rowErrors[row.id];
              const invalid = isRowIncomplete(row) || Boolean(rowError);
              const hasTin = !!row.customerTin;
              const hasAddress = !!row.customerAddress;

              const mainRow = (
                <tr
                  key={row.id}
                  ref={(node) => { rowWrapperRefs.current[row.id] = node; }}
                  className="hover:bg-ink-canvas/60"
                  data-row-id={row.id}
                  data-invalid={invalid ? "true" : undefined}
                >
                  {/* EXPAND TOGGLE */}
                  <td className={`px-2 py-2 text-center align-top ${invalid ? "border-l-2 border-danger-400" : ""}`}>
                    <button
                      type="button"
                      onClick={() => toggleRowExpanded(row.id)}
                      className="text-ink-body hover:text-ink"
                      aria-label={isExpanded ? "Collapse row detail" : "Expand row detail"}
                    >
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </td>

                  {/* MONTH */}
                  <td className="px-3 py-2 align-top">
                    <select
                      ref={(node) => { monthSelectRefs.current[row.id] = node; }}
                      value={row.month || ""}
                      onChange={(e) => handleMonthChange(row.id, e.target.value)}
                      className="w-full border border-ink-line rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="" disabled>Select</option>
                      {ALL_MONTH_OPTIONS.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </td>

                  {/* DESCRIPTION / COA */}
                  <td className="px-3 py-2 align-top">
                    <ChartOfAccountsSearch
                      value={row.chartOfAccountName || row.description}
                      onChange={(value) => handleDescriptionInput(value, row.id)}
                      onSelect={(account) => handleChartOfAccountSelect(account, row.id)}
                      placeholder="Search or enter description"
                      showLinkedBadge={false}
                      clearTrigger={clearSearchTrigger}
                    />
                    {row.chartOfAccount && (
                      <div className="text-xs text-primary-700 mt-0.5">Linked to chart of account</div>
                    )}
                    {rowError && (
                      <p role="alert" className="text-xs text-danger-500 mt-0.5">{rowError}</p>
                    )}
                  </td>

                  {/* CUSTOMER */}
                  <td className="px-3 py-2 align-top">
                    <CustomerSearch
                      value={row.customer}
                      onSelect={(customer) => handleCustomerSelect(row.id, customer)}
                      placeholder="Search customer *"
                      required
                    />
                    {row.customer && (
                      <div className="text-xs text-ink-body mt-0.5 flex gap-2">
                        {hasTin ? <span>TIN ✓</span> : <span className="text-warning-500">TIN missing</span>}
                        {hasAddress ? <span>Addr ✓</span> : <span className="text-warning-500">Addr missing</span>}
                      </div>
                    )}
                  </td>

                  {/* GROSS */}
                  <td className="px-3 py-2 align-top">
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-ink-body text-xs">₱</span>
                      <MoneyInput
                        value={row.grossAmount}
                        onValueChange={(value) => handleGrossAmountChange(row.id, value)}
                        className="pl-6 w-full"
                        disabled={isExempt}
                      />
                    </div>
                  </td>

                  {/* OUTPUT VAT (readonly) */}
                  <td className="px-3 py-2 align-top">
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-ink-body text-xs">₱</span>
                      <input
                        type="text"
                        value={formatMoney(row.vatAmount)}
                        readOnly
                        className="pl-6 w-full border border-ink-line rounded px-2 py-1.5 text-sm text-right tabular-nums bg-ink-canvas text-ink-body"
                      />
                    </div>
                  </td>

                  {/* VAT EXEMPT */}
                  <td className="px-3 py-2 align-top">
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-ink-body text-xs">₱</span>
                      <MoneyInput
                        value={row.vatExempt}
                        onValueChange={(value) => handleVatExemptChange(row.id, value)}
                        className="pl-6 w-full"
                      />
                    </div>
                  </td>

                  {/* ACTION */}
                  <td className="px-2 py-2 text-center align-top">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeRow(row.id);
                      }}
                      className="text-danger-500 hover:text-danger-700 disabled:text-ink-line transition-colors duration-200"
                      disabled={rows.length <= 5}
                      title={rows.length <= 5 ? "Minimum 5 rows required" : "Remove row"}
                      aria-label="Remove row"
                    >
                      <Trash2 className="w-4 h-4 mx-auto" />
                    </button>
                  </td>
                </tr>
              );

              if (!isExpanded) return [mainRow];

              const detailRow = (
                <tr key={`${row.id}-detail`}>
                  <td colSpan={8} className="bg-ink-canvas px-6 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-ink-body mb-1">Customer TIN</label>
                        <input
                          type="text"
                          placeholder="Customer TIN (optional)"
                          value={row.customerTin ?? ""}
                          onChange={(e) => handleCustomerTinChange(row.id, e.target.value)}
                          className={`w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                            row.customerTinLocked ? 'bg-ink-canvas text-ink-body cursor-not-allowed' : 'bg-white'
                          }`}
                          readOnly={!!row.customerTinLocked}
                        />
                        {row.customer && !row.customerTinLocked && (
                          <p className="text-xs text-warning-500 mt-1">No TIN on file — you can enter one here.</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-ink-body mb-1">Customer classification</label>
                        <select
                          value={row.customerClassification ?? ""}
                          onChange={(e) => handleCustomerClassificationChange(row.id, e.target.value)}
                          className="w-full border border-ink-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">— Select —</option>
                          <option value="corporation">Corporation</option>
                          <option value="individual">Individual</option>
                          <option value="government">Government</option>
                        </select>
                      </div>

                      <div className="md:col-span-2">
                        <label
                          htmlFor={`atc-out-${row.id}`}
                          className="block text-xs font-medium text-ink-body mb-1"
                        >
                          ATC <span className="font-normal">(optional — a 2307 is generated automatically when you post)</span>
                        </label>
                        {(row.atcs || [null]).map((selected, atcIndex) => (
                          <div key={atcIndex} className="flex items-start gap-2 mb-2">
                            <div className="flex-1">
                              <AtcSearchSelect
                                inputId={`atc-out-${row.id}-${atcIndex}`}
                                formType="2307"
                                // 2307 ATCs key to the PAYEE. On an output/sales row the
                                // client is the payee and the customer is the PAYOR, so the
                                // class follows the filer's own entity type — narrowing by
                                // the customer offered the wrong half of every WI###/WC###
                                // pair. Same derivation as Form2307Form.jsx.
                                payeeClass={isCorporateFiler(client?.clientType) ? 'corporation' : 'individual'}
                                value={selected?._id || null}
                                onChange={(atc) => handleAtcChange(row.id, atcIndex, atc)}
                              />
                            </div>
                            {(row.atcs || []).length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveAtc(row.id, atcIndex)}
                                className="px-2 py-2 text-xs text-danger-500 hover:text-danger-700"
                                aria-label={`Remove ATC ${atcIndex + 1}`}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => handleAddAtc(row.id)}
                          className="text-xs font-medium text-primary-500 hover:text-primary-700"
                        >
                          + Add another withholding
                        </button>
                        {row.customerClassification === 'government' && (row.atcs || []).filter(Boolean).length < 2 && (
                          <p className="text-xs text-warning-500 mt-1">
                            Government payors withhold twice — expanded withholding tax plus the 5% creditable VAT withholding (WV010 goods / WV020 services). Add the second ATC.
                          </p>
                        )}
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-ink-body mb-1">Customer address</label>
                        <input
                          type="text"
                          placeholder={row.customer ? "Address from customer record" : "Customer address (optional)"}
                          value={row.customerAddress ?? ""}
                          onChange={(e) => handleCustomerAddressChange(row.id, e.target.value)}
                          className={`w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                            row.customerAddressLocked ? 'bg-ink-canvas text-ink-body cursor-not-allowed' : 'bg-white'
                          }`}
                          readOnly={!!row.customerAddressLocked}
                        />
                        {row.customer && !row.customerAddressLocked && (
                          <p className="text-xs text-warning-500 mt-1">
                            No address on file — you can enter one here, or update it permanently in Customer Maintenance.
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm text-ink-body">
                          <input
                            type="checkbox"
                            checked={row.isService ?? false}
                            onChange={(e) => handleIsServiceChange(row.id, e.target.checked)}
                            className="h-4 w-4 rounded border-ink-line text-primary-500 focus:ring-primary-500"
                          />
                          Service transaction
                        </label>
                        <label className="flex items-center gap-2 text-sm text-ink-body">
                          <input
                            type="checkbox"
                            checked={row.isZeroRated ?? false}
                            onChange={(e) => handleIsZeroRatedChange(row.id, e.target.checked)}
                            className="h-4 w-4 rounded border-ink-line text-primary-500 focus:ring-primary-500"
                          />
                          Zero-rated sale
                        </label>
                      </div>

                      <div>
                        <div className="text-xs text-ink-body">VAT exclusive (net)</div>
                        <div className="text-sm text-ink tabular-nums">₱{formatMoney(row.netAmount)}</div>
                      </div>
                    </div>
                  </td>
                </tr>
              );

              return [mainRow, detailRow];
            })}
          </tbody>

          <tfoot className="bg-ink-canvas border-t-2 border-ink-line">
            <tr>
              <td className="px-2 py-3"></td>
              <td colSpan={3} className="px-3 py-3 text-sm font-medium text-ink-body">
                Totals ({filledRowsCount} filled, {rows.length - filledRowsCount} empty)
                {incompleteRowsCount > 0 && (
                  <span className="ml-2 text-danger-500">{incompleteRowsCount} incomplete</span>
                )}
              </td>
              <td className="px-3 py-3 text-sm font-medium text-ink text-right tabular-nums">₱{formatMoney(totals.gross)}</td>
              <td className="px-3 py-3 text-sm font-medium text-ink text-right tabular-nums">₱{formatMoney(totals.vat)}</td>
              <td className="px-3 py-3 text-sm font-medium text-ink text-right tabular-nums">₱{formatMoney(totals.vatExempt)}</td>
              <td className="px-2 py-3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Actions */}
      <div className="mt-4 flex justify-between items-center">
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            addRows();
          }}
          variant="outline"
          size="sm"
          icon={Plus}
        >
          Add 5 more rows
        </Button>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              clearAllRows();
            }}
            variant="dangerOutline"
            size="md"
          >
            Clear all
          </Button>

          <DraftStatusIndicator lastSavedAt={draft.lastSavedAt} isSaving={draft.isSaving} />

          <div className="text-right">
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                runSaveSafely();
              }}
              disabled={loading || !isClientAvailable || !isFiscalYearAvailable}
              loading={loading}
              variant="primary"
              size="md"
            >
              {loading ? "Saving…" : `Save ${filledRowsCount} record${filledRowsCount !== 1 ? "s" : ""} (FY ${fiscalYearNum || "—"})`}
            </Button>
            <p className="text-xs text-ink-body mt-1">Empty rows are skipped; incomplete rows stay in the grid</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VATOutputForm;
